import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/api.config.jsx';
import { hashPassword } from '../utils/passwordUtils.jsx';
import { trackEvent, EVENT_TYPE_PAGE } from '../utils/trackEvent.jsx';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";

const SignIn = () => {
  useEffect(() => {
    trackEvent(EVENT_TYPE_PAGE);
  }, []);

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  const handleChange = (e) => {
    setError('');
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const saltResponse = await fetch(`${getApiUrl()}/auth/auth/salt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email
        })
      });

        if (!saltResponse.ok) {
          throw new Error('Invalid email or password');
        }

        const saltResponseData = await saltResponse.json();

        // Handle both response structures: {salt: "..."} and {data: {salt: "..."}}
        let salt;
        if (saltResponseData && saltResponseData.salt) {
          // Direct salt structure: {salt: "..."}
          salt = saltResponseData.salt;
        } else if (saltResponseData && saltResponseData.data && saltResponseData.data.salt) {
          // Nested salt structure: {data: {salt: "..."}}
          salt = saltResponseData.data.salt;
        } else {
          throw new Error('Invalid salt response from server');
        }

        const hashedPassword = await hashPassword(formData.password, salt);

      const response = await fetch(`${getApiUrl()}/auth/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: hashedPassword,
          clientHash: true
        }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Invalid email or password');
      }

      if (data.data && data.data.user && data.data.user.userId) {
        localStorage.setItem('userId', data.data.user.userId);
      }

      const jwt = data?.data?.data?.token || data?.data?.token || data?.token;
      if (jwt) {
        localStorage.setItem('token', jwt);
      }

      localStorage.setItem('isLoggedIn', 'true');
      setFormData({ email: '', password: '' });
      
      // Redirect admin user to CRM page
      if (formData.email === 'rumi.sabina@gmail.com') {
        navigate('/crm/admin');
      } else {
        navigate('/personal-accounts');
      }
    } catch (error) {
      setError(error.message || 'Sign in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${getApiUrl()}/auth/auth/reset-password/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail })
      });

      if (!response.ok) {
        throw new Error('Password reset request failed');
      }

      alert('If an account exists with this email, you will receive password reset instructions.');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      setError('Failed to process password reset request');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        {!showForgotPassword ? (
          <>
            <CardHeader>
              <h2 className="text-2xl font-bold text-center text-gray-900">Welcome Back</h2>
              <p className="text-center text-gray-600">Sign in to access your HalalCred account</p>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="mb-4 p-3 text-sm text-red-500 bg-red-50 rounded-md">
                  {error}
                </div>
              )}

              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <Input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                    className="w-full"
                  />
                </div>

                <div>
                  <Input
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                    autoComplete="current-password"
                    className="w-full"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full"
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <Button
                variant="link"
                onClick={() => setShowForgotPassword(true)}
                disabled={isLoading}
                className="text-primary"
              >
                Forgot Password?
              </Button>
              <p className="text-center text-gray-600">
                Don't have an account?{' '}
                <Link to="/signup" className="text-primary hover:underline">
                  Sign Up
                </Link>
              </p>
            </CardFooter>
          </>
        ) : (
          <>
            <CardHeader>
              <h2 className="text-2xl font-bold text-center text-gray-900">Reset Password</h2>
              <p className="text-center text-gray-600">Enter your email to receive reset instructions</p>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="mb-4 p-3 text-sm text-red-500 bg-red-50 rounded-md">
                  {error}
                </div>
              )}

              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Input
                    type="email"
                    placeholder="Email Address"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    disabled={isLoading}
                    className="w-full"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            </CardContent>
            <CardFooter>
              <Button
                variant="outline"
                onClick={() => setShowForgotPassword(false)}
                disabled={isLoading}
                className="w-full"
              >
                Back to Sign In
              </Button>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
};

export default SignIn; 